import { z } from 'zod'

import {
  AppNotificationAudience,
  AppNotificationPlatform,
  AppNotificationTag,
} from '../constants/appNotification'
import { dbEntitySchema } from './system'

export const appNotificationInAppLinkSchema = z.object({
  routeName: z.string().min(1),
  query: z
    .record(z.union([z.string(), z.array(z.string())]))
    .optional()
    .nullable(),
})

export const appNotificationContentSchema = z.object({
  title: z.string().min(1),
  content: z.string(),
  previewText: z.string().optional().nullable(),
  image: z.string().optional().nullable(),
  tags: z.array(z.nativeEnum(AppNotificationTag)).default([]),
  audiences: z
    .array(z.nativeEnum(AppNotificationAudience))
    .min(1)
    .default([
      AppNotificationAudience.STAFF,
      AppNotificationAudience.PROMOTER,
      AppNotificationAudience.PARTNER,
    ]),
  platforms: z
    .array(z.nativeEnum(AppNotificationPlatform))
    .min(1)
    .default([AppNotificationPlatform.WEB]),
  inAppLink: appNotificationInAppLinkSchema.optional().nullable(),
})

export const appNotificationSchema = dbEntitySchema.extend({
  title: z.string(),
  content: z.string(),
  contentSummary: z.string().optional().default(''),
  previewText: z.string().optional().default(''),
  image: z.string().optional().nullable(),
  tags: z.array(z.nativeEnum(AppNotificationTag)).default([]),
  audiences: z
    .array(z.nativeEnum(AppNotificationAudience))
    .min(1)
    .default([
      AppNotificationAudience.STAFF,
      AppNotificationAudience.PROMOTER,
      AppNotificationAudience.PARTNER,
    ]),
  platforms: z
    .array(z.nativeEnum(AppNotificationPlatform))
    .min(1)
    .default([AppNotificationPlatform.WEB]),
  inAppLink: appNotificationInAppLinkSchema.optional().nullable(),
  draft: appNotificationContentSchema.optional().nullable(),
  published: appNotificationContentSchema.optional().nullable(),
  publishedAt: z.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
})

// App notification list item (for management user inbox, with read state)
export const appNotificationItemSchema = appNotificationSchema.extend({
  readAt: z.date().optional().nullable(),
  managementUser: z.string().optional().nullable(),
})

export const appNotificationsListQuerySchema = z.object({
  managementUserId: z.string().optional().nullable(),
  audience: z.nativeEnum(AppNotificationAudience).optional().nullable(),
  platform: z.nativeEnum(AppNotificationPlatform).optional().nullable(),
  tags: z
    .union([
      z.array(z.nativeEnum(AppNotificationTag)),
      z.nativeEnum(AppNotificationTag),
    ])
    .optional()
    .nullable()
    .transform((v) => (v == null ? undefined : Array.isArray(v) ? v : [v])),
  skip: z.coerce.number().min(0).optional().nullable(),
  limit: z.coerce.number().min(0).optional().nullable(),
  sort: z.string().optional().nullable(),
})

export const appNotificationsListResponseSchema = z.object({
  data: z.array(appNotificationItemSchema),
})

// Admin create/update body
export const appNotificationCreateBodySchema = z.object({
  draft: appNotificationContentSchema,
})

export const appNotificationUpdateDraftBodySchema =
  appNotificationContentSchema.partial()

// Legacy alias to preserve existing imports during migration.
export const appNotificationUpdateBodySchema =
  appNotificationUpdateDraftBodySchema

export const appNotificationPublishBodySchema = z.object({
  publishedAt: z.coerce.date().optional().nullable(),
})
